import { MobileDevice } from '../../../../../types';
import GeneralDialog from '../../../../shared/general-dialog';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Chip from '@mui/material/Chip';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Check from '@mui/icons-material/Check';
import Close from '@mui/icons-material/Close';
import DeleteSweep from '@mui/icons-material/DeleteSweep';
import DoNotDisturb from '@mui/icons-material/DoNotDisturb';
import FolderDelete from '@mui/icons-material/FolderDelete';
import Pending from '@mui/icons-material/Pending';
import PhoneIphoneRounded from '@mui/icons-material/PhoneIphoneRounded';
interface UserDeviceDialogProps {
  open: boolean;
  handleClose: () => void;
  selectedDevice: MobileDevice;
}

export default function UserDeviceDialog({
  open,
  handleClose,
  selectedDevice,
}: UserDeviceDialogProps) {
  return (
    <GeneralDialog
      open={open}
      handleClose={handleClose}
      title="Device Details"
      maxWidth="lg"
      dialogContent={
        <>
          <Stack
            flexDirection={'row'}
            sx={{ px: 6 }}
            gap={2}
            alignItems={'stretch'}
          >
            <Card
              sx={{
                width: 300,
              }}
            >
              <CardContent>
                <Stack flexDirection={'row'} gap={2} sx={{ pt: 5 }}>
                  <PhoneIphoneRounded sx={{ width: 40, height: 40 }} />
                  <Stack flexDirection={'column'}>
                    <Typography variant="h5">{selectedDevice.model}</Typography>
                    <Typography variant="body2">{selectedDevice.os}</Typography>
                    <Typography variant="body2">
                      {selectedDevice.name}
                    </Typography>
                    {selectedDevice.status === 'APPROVED' ? (
                      <Chip
                        variant="outlined"
                        icon={<Check />}
                        label="approved"
                        color="success"
                        size="small"
                        sx={{ width: 'fit-content' }}
                      />
                    ) : selectedDevice.status === 'BLOCKED' ? (
                      <Chip
                        variant="outlined"
                        icon={<DoNotDisturb />}
                        label={'blocked'}
                        color={'warning'}
                        size="small"
                        sx={{ width: 'fit-content' }}
                      />
                    ) : selectedDevice.status === 'UNKNOWN' ||
                      !selectedDevice.status ? (
                      <Chip
                        variant="outlined"
                        icon={<FolderDelete />}
                        label={'account wiped'}
                        color={'error'}
                        size="small"
                        sx={{ width: 'fit-content' }}
                      />
                    ) : selectedDevice.status === 'WIPED' ? (
                      <Chip
                        variant="outlined"
                        icon={<DeleteSweep />}
                        label={'wiped'}
                        size="small"
                        color="error"
                        sx={{ width: 'fit-content' }}
                      />
                    ) : selectedDevice.status === 'PENDING' ? (
                      <Chip
                        variant="outlined"
                        icon={<Pending />}
                        label={'pending'}
                        size="small"
                        color="warning"
                        sx={{ width: 'fit-content' }}
                      />
                    ) : (
                      <Chip
                        label={selectedDevice.status.toLowerCase()}
                        sx={{ width: 'fit-content' }}
                        icon={<Close />}
                        size="small"
                        color="info"
                        variant="outlined"
                      />
                    )}
                    <Typography sx={{ fontSize: 10 }}>
                      Last Sync:{' '}
                      {new Date(selectedDevice.lastSync.value).toLocaleString()}
                    </Typography>
                  </Stack>
                </Stack>
              </CardContent>
            </Card>
            <Stack flexDirection={'column'} gap={2}>
              <Card sx={{ width: 800 }}>
                <CardContent>
                  <Typography variant="subtitle1" sx={{ color: 'grey' }}>
                    Device Information
                  </Typography>
                  <Stack
                    flexDirection={'row'}
                    justifyContent={'baseline'}
                    mt={3}
                    ml={3}
                    gap={6}
                  >
                    <Stack flexDirection={'column'}>
                      <Typography
                        sx={{
                          fontWeight: 600,
                        }}
                      >
                        Device ID
                      </Typography>
                      <Typography
                        noWrap
                        sx={{
                          maxWidth: 200,
                          textOverflow: 'ellipsis',
                        }}
                      >
                        {selectedDevice.deviceId}
                      </Typography>
                    </Stack>
                    <Stack flexDirection={'column'}>
                      <Typography sx={{ fontWeight: 600 }}>Type</Typography>
                      <Typography>{selectedDevice.type}</Typography>
                    </Stack>
                    <Stack flexDirection={'column'}>
                      <Typography sx={{ fontWeight: 600 }}>
                        Device OS
                      </Typography>
                      <Typography>{selectedDevice.os}</Typography>
                    </Stack>
                    <Stack flexDirection={'column'}>
                      <Typography sx={{ fontWeight: 600 }}>
                        Device Model
                      </Typography>
                      <Typography>{selectedDevice.model}</Typography>
                    </Stack>
                  </Stack>
                  <Stack
                    flexDirection={'row'}
                    justifyContent={'baseline'}
                    mt={3}
                    ml={3}
                    gap={6}
                  >
                    <Stack flexDirection={'column'}>
                      <Typography sx={{ fontWeight: 600 }}>
                        First Sync
                      </Typography>
                      <Typography>
                        {new Date(
                          selectedDevice.firstSync.value
                        ).toLocaleString()}
                      </Typography>
                    </Stack>
                    <Stack flexDirection={'column'}>
                      <Typography sx={{ fontWeight: 600 }}>
                        Last Sync
                      </Typography>
                      <Typography>
                        {new Date(
                          selectedDevice.lastSync.value
                        ).toLocaleString()}
                      </Typography>
                    </Stack>
                    {selectedDevice.userAgent.length > 0 ? (
                      <Stack flexDirection={'column'}>
                        <Typography sx={{ fontWeight: 600 }}>
                          User Agent
                        </Typography>
                        <Typography
                          noWrap
                          sx={{
                            maxWidth: 150,
                            textOverflow: 'ellipsis',
                          }}
                        >
                          {selectedDevice.userAgent}
                        </Typography>
                      </Stack>
                    ) : null}
                    {selectedDevice.hardwareId.length > 0 ? (
                      <Stack flexDirection={'column'}>
                        <Typography sx={{ fontWeight: 600 }}>
                          Serial Number
                        </Typography>
                        <Typography>{selectedDevice.hardwareId}</Typography>
                      </Stack>
                    ) : null}
                  </Stack>
                </CardContent>
              </Card>
            </Stack>
          </Stack>
          <Stack sx={{ px: 6 }}>
            <Card sx={{ mt: 3 }}>
              <CardContent>
                <Typography variant="subtitle1" sx={{ color: 'grey' }}>
                  Applications
                </Typography>
                <Stack flexDirection={'row'} mt={3} ml={3} gap={6}>
                  {selectedDevice.applications?.length === 0 ? (
                    <Stack flexDirection={'column'}>
                      <Typography>No Applications Found</Typography>
                    </Stack>
                  ) : (
                    <Stack
                      flexDirection={'row'}
                      gap={4}
                      flexWrap={'wrap'}
                      height={200}
                      overflow={'auto'}
                    >
                      {selectedDevice.applications?.map((app) => (
                        <Stack
                          flexDirection={'column'}
                          flexWrap={'wrap'}
                          key={app.packageName}
                        >
                          <Typography sx={{ fontWeight: 600 }}>
                            Display Name
                          </Typography>
                          <Typography>{app.displayName}</Typography>
                        </Stack>
                      ))}
                    </Stack>
                  )}
                </Stack>
              </CardContent>
            </Card>
          </Stack>
        </>
      }
      primaryButton={{
        loading: false,
        action: handleClose,
        disabled: false,
        title: 'Close',
      }}
    />
  );
}
